import { FiEdit } from "react-icons/fi";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
`;

export const PageSection = styled.div`
  display: block;
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const PageTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.8rem;
  font-weight: bold;
  margin: 0;
`;

export const AnalyticsContainer = styled.div`
  margin-bottom: 3rem;
`;

export const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  background: ${(props) => (props.$active ? "#ff6b35" : "transparent")};
  color: ${(props) => (props.$active ? "#ffffff" : "#cccccc")};

  &:hover {
    background: ${(props) => (props.$active ? "#e55a2b" : "#444")};
    color: #ffffff;
  }
`;

export const BandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  margin-top: 1rem;
`;

export const BandName = styled.div`
  color: #ffffff;
  font-weight: bold;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
  position: relative;
  z-index: 2;
`;

export const BandGenre = styled.div`
  color: #ff6b35;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-shadow: 1px 1px 3px rgba(0,0,0,0.8);
  position: relative;
  z-index: 2;
`;

export const ProductStats = styled.div`
  color: #cccccc;
  font-size: 0.8rem;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
  position: relative;
  z-index: 2;
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
`;

export const PaginationButton = styled.button`
  padding: 0.8rem 1.2rem;
  border: 2px solid #ff6b35;
  border-radius: 8px;
  background: transparent;
  color: #ff6b35;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: #ff6b35;
    color: white;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    font-size: 1.1rem;
  }
`;

export const PageInfo = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
  font-weight: 500;

  .current-page {
    color: #ff6b35;
    font-weight: bold;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 15px;
  padding: 1.5rem;
  border: 2px solid ${(props) => (props.$selected ? "#ff6b35" : "#444")};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;
  cursor: ${(props) => (props.onClick ? "pointer" : "default")};
  transform: ${(props) =>
    props.$selected ? "translateY(-3px)" : "translateY(0)"};
  box-shadow: ${(props) =>
    props.$selected
      ? "0 10px 25px rgba(255, 107, 53, 0.3)"
      : "0 5px 15px rgba(0, 0, 0, 0.2)"};

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35, #e55a2b);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(255, 107, 53, 0.2);
    border-color: #ff6b35;
  }
`;

// New styled component for band card with background image
export const BandStatCard = styled(StatCard)`
  ${props => props.$backgroundImage ? `
    background-image: linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.9) 100%), url(${props.$backgroundImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    
    &:hover {
      background-image: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.8) 100%), url(${props.$backgroundImage});
    }
  ` : `
    background: linear-gradient(135deg, rgba(30,30,30,0.9) 0%, rgba(50,50,50,0.8) 100%);
  `}
`;

export const StatIcon = styled.div`
  font-size: 2.5rem;
  color: #ff6b35;
  margin-bottom: 1rem;
  display: flex;
  animation: ${float} 3s ease-in-out infinite;
  align-items: center;
`;

export const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

export const StatLabel = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
  margin-top: ${(props) => (props.$eur ? "-10px" : "0px")};
  margin-bottom: 0.5rem;
`;

export const StatChange = styled.div`
  font-size: 0.8rem;
  margin-top: 0.5rem;
  color: ${(props) => (props.$positive ? "#27ae60" : "#e74c3c")};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 600;
`;

export const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 2rem;

  @media (max-width: 1510px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

export const ChartContainer = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid #444;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff6b35;
    transform: translateY(-2px);
  }
`;

export const ChartTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const ChartPlaceholder = styled.div`
  min-height: 250px;
  background: linear-gradient(45deg, #333, #444);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cccccc;
  font-size: 0.9rem;
  padding: 1rem;
  text-align: center;

  h4 {
    color: #ff6b35;
    margin-bottom: 1rem;
    font-size: 1.1rem;
  }

  p {
    margin: 0.5rem 0;
    color: #cccccc;
  }
`;

export const TopProductsContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 400px;
  gap: 1rem;
  overflow-y: auto;
  padding-top: 0.5rem;
  padding-right: 0.5rem;
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #333;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ff6b35;
    border-radius: 3px;
  }
`;

export const ProductItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 8px;
  border-left: 3px solid #ff6b35;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateX(3px);
  }
`;

export const ProductName = styled.div`
  color: #ffffff;
  font-weight: 600;
  margin-bottom: 0.3rem;
  font-size: 0.95rem;
`;

export const ProductCategory = styled.div`
  color: #ff6b35;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

export const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
`;

export const MetricCard = styled.div`
  background: rgba(255, 107, 53, 0.1);
  border: 1px solid rgba(255, 107, 53, 0.3);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 53, 0.15);
    border-color: rgba(255, 107, 53, 0.5);
  }
`;

export const MetricLabel = styled.div`
  color: #ff6b35;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

export const MetricValue = styled.div`
  color: #ffffff;
  font-size: 0.85rem;
  margin-bottom: 0.2rem;

  &:last-child {
    margin-bottom: 0;
    font-weight: 600;
  }
`;

export const ProductAnalytics = styled.div`
  background: #1a1a1a;
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid #333;
  margin-bottom: 2rem;
`;

export const PerformanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

export const PerformanceCard = styled.div`
  background: rgba(255, 107, 53, 0.05);
  border: 1px solid rgba(255, 107, 53, 0.2);
  border-radius: 10px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 53, 0.1);
    border-color: rgba(255, 107, 53, 0.4);
    transform: translateY(-2px);
  }
`;

export const PerformanceIcon = styled.div`
  font-size: 2rem;
  color: #ff6b35;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: center;
`;

export const PerformanceValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 0.3rem;
`;

export const PerformanceLabel = styled.div`
  color: #cccccc;
  font-size: 0.85rem;
  margin-bottom: 1rem;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #333;
  border-radius: 3px;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #ff6b35, #e55a2b);
  border-radius: 3px;
  width: ${(props) => Math.min(Math.max(props.$percentage, 0), 100)}%;
  transition: width 1s ease-in-out;
`;

export const InventoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
  padding-top: 0.5rem;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #333;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ff6b35;
    border-radius: 3px;
  }
`;

export const InventoryCard = styled.div`
  background: ${(props) => {
    switch (props.$status) {
      case "out":
        return "rgba(231, 76, 60, 0.1)";
      case "low":
        return "rgba(243, 156, 18, 0.1)";
      default:
        return "rgba(39, 174, 96, 0.1)";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$status) {
        case "out":
          return "rgba(231, 76, 60, 0.3)";
        case "low":
          return "rgba(243, 156, 18, 0.3)";
        default:
          return "rgba(39, 174, 96, 0.3)";
      }
    }};
  border-radius: 10px;
  padding: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${(props) => {
      switch (props.$status) {
        case "out":
          return "rgba(231, 76, 60, 0.5)";
        case "low":
          return "rgba(243, 156, 18, 0.5)";
        default:
          return "rgba(39, 174, 96, 0.5)";
      }
    }};
  }
`;

export const InventoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

export const InventoryIcon = styled.div`
  font-size: 1.5rem;
  color: ${(props) => {
    switch (props.$status) {
      case "out":
        return "#e74c3c";
      case "low":
        return "#f39c12";
      default:
        return "#27ae60";
    }
  }};
  display: flex;
  align-items: center;
`;

export const InventoryTitle = styled.div`
  color: #ffffff;
  font-weight: 600;
  font-size: 0.9rem;
`;

export const InventoryStats = styled.div`
  margin-bottom: 1rem;

  div {
    color: #cccccc;
    font-size: 0.8rem;
    margin-bottom: 0.3rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    strong {
      color: #ffffff;
    }
  }
`;
export const EditIcon = styled(FiEdit)`
  cursor: pointer;
  margin-bottom: 5px;
  transition: transform 0.3s ease-in-out;
  &:hover {
    transform: scale(1.05);
    color: #ff6b35;
  }
`;

export const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const OriginalPrice = styled.span`
  color: #999999;
  text-decoration: line-through;
  font-size: 0.75rem;
`;

export const SalePrice = styled.strong`
  color: #ff6b35;
  font-weight: bold;
`;

export const StockAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${(props) => {
    switch (props.$status) {
      case "out":
        return "rgba(231, 76, 60, 0.2)";
      case "low":
        return "rgba(243, 156, 18, 0.2)";
      default:
        return "rgba(39, 174, 96, 0.2)";
    }
  }};
  color: ${(props) => {
    switch (props.$status) {
      case "out":
        return "#e74c3c";
      case "low":
        return "#f39c12";
      default:
        return "#27ae60";
    }
  }};
`;

export const AlertIcon = styled.div`
  font-size: 1rem;
  display: flex;
  align-items: center;
`;

export const SizeStockDisplay = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

export const SizeStockTitle = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  font-weight: bold;
`;

export const SizeStockList = styled.div`
  display: flex;
  flex-wrap: wrap;

  gap: 0.5rem;
`;

export const SizeStockBadge = styled.span`
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: bold;
  color: ${(props) => {
    switch (props.$alertLevel) {
      case "critical":
        return "#c0392b"; // Red text
      case "warning":
        return "#e67e22"; // Orange text
      default:
        return "#229954"; // Green text
    }
  }};
  background: ${(props) => {
    switch (props.$alertLevel) {
      case "critical":
        return "#e74c3c25"; // Red background
      case "warning":
        return "#f39c1225"; // Orange background
      default:
        return "#27ae6025"; // Green background
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$alertLevel) {
        case "critical":
          return "#c0392b"; // Red border
        case "warning":
          return "#e67e22"; // Orange border
        default:
          return "#229954"; // Green border
      }
    }};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;
